import { Box, Tooltip, Typography } from '@mui/material';
import { TIME_FILTER_TABS, TimeFilterTabs } from '@/utilities/etransportTypes';
import { useTranslation } from 'react-i18next';
import { SearchComponent } from '@/components/common/Search';
import InfoIcon from '@mui/icons-material/Info';
import { CategoryCountSelector } from '@/components/common/selection/CategoryCountSelector';
import './etransport.css';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';

export function FilterBar({
  isLoading,
  activeTab,
  categories,
  searchTarget,
  setActiveTab,
  onSearchChange,
}: {
  isLoading: boolean;
  activeTab: TimeFilterTabs;
  categories: Map<TimeFilterTabs, number>;
  searchTarget: string | undefined;
  setActiveTab: (tab: TimeFilterTabs) => void;
  onSearchChange: (value: string | undefined) => void;
}) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        alignItems: 'flex-start',
      }}
    >
      <Box width="100%" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ color: 'black' }}>
          {t('mainMenu.etransport')}
        </Typography>
        <Tooltip title={t('etransport.filterbar.info')} arrow>
          <InfoIcon sx={{ fontSize: '1.5rem', color: 'text.secondary', cursor: 'help' }} />
        </Tooltip>
      </Box>
      <Box width="100%">
        <TargetSearch searchTarget={searchTarget} onSearchChange={onSearchChange} />
      </Box>

      <Box
        sx={{
          paddingLeft: isPhoneOrHighScreen() ? '7px' : '0px',
        }}
      >
        <TimeFilter isLoading={isLoading} activeTab={activeTab} categories={categories} setActiveTab={setActiveTab} />
      </Box>
    </Box>
  );
}

function TargetSearch({
  searchTarget,
  onSearchChange,
}: {
  searchTarget: string | undefined;
  onSearchChange: (value: string | undefined) => void;
}) {
  const { t } = useTranslation();
  return (
    <SearchComponent
      searchText={searchTarget}
      setSearchText={onSearchChange}
      placeholder={t('etransport.filterbar.target.platesearch')}
      showHistory={true}
    />
  );
}

function TimeFilter({
  isLoading,
  activeTab,
  categories,
  setActiveTab,
}: {
  isLoading: boolean;
  activeTab: TimeFilterTabs;
  categories: Map<TimeFilterTabs, number>;
  setActiveTab: (tab: TimeFilterTabs) => void;
}) {
  const { t, i18n } = useTranslation();
  const DEFAULT_WIDTH: string = '175px';
  const badgeColors = new Map<TimeFilterTabs, string>([
    ['All', '#333333'],
    ['Inactive', '#bdbdbd'],
    ['Active', 'var(--mui-palette-secondary-main)'],
    ['Future', 'var(--mui-palette-primary-main)'],
  ]);

  function getLabel(label: TimeFilterTabs): string {
    return t(`etransport.filterbar.freight.${label.toLowerCase()}`);
  }

  //just add the exceptions here
  const minWidthByLocale: Record<string, string> = {
    ro: '210px',
    cz: '200px',
  };

  return (
    <CategoryCountSelector
      options={TIME_FILTER_TABS}
      minWidth={minWidthByLocale[i18n.language] ?? DEFAULT_WIDTH}
      isLoading={isLoading}
      activeTab={activeTab}
      badgeColors={badgeColors}
      numOfCategories={categories}
      getLabel={getLabel}
      setActiveTab={setActiveTab}
    />
  );
}
